import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import {
  PDKTitleNotPresentIcon,
  PDKTitlePresentIcon,
} from '@/components/ui/pdk-icons';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Trade } from '@/models/registry';
import { tradeStore } from '@/models/stores/trades';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import TitlePresentBadge from './title-present-badge';

export const getTitlePresentOptionsMap = (
  className: Record<string, boolean>,
): (Option<'yes' | 'no'> & {
  booleanValue: boolean;
})[] => [
  {
    value: 'yes',
    label: 'Yes',
    icon: (
      <PDKTitlePresentIcon
        className={cn('size-3.5 cursor-pointer', className)}
      />
    ),
    booleanValue: true,
    abbreviation: 'Title Present',
  },
  {
    value: 'no',
    label: 'No',
    icon: <PDKTitleNotPresentIcon className="size-3.5" />,
    booleanValue: false,
    abbreviation: 'Title Not Present',
  },
];

export type TitlePresentSelectorProps = {
  tradeId: Trade['id'];
  withHotkeyListener?: boolean;
  useAbbreviation?: boolean;
  className?: string;
  titlePresent: boolean;
  setTitlePresent: (titlePresent: boolean) => void;
};

export function TitlePresentSelector({
  tradeId,
  withHotkeyListener = false,
  titlePresent,
  setTitlePresent,
  useAbbreviation = false,
  className,
}: TitlePresentSelectorProps) {
  const updateTitlePresentMutation = useMutation({
    onMutate: async ({ titlePresent }: { titlePresent: boolean }) => {
      tradeStore.update({
        id: tradeId,
        trade: { title_present: titlePresent },
      });
    },
    mutationFn: async ({ titlePresent }: { titlePresent: boolean }) => {
      const response = await pdkAxios.post(
        `/trades/${tradeId}/title_present`,
        // /api/trades/:trade_id/title_present
        {
          title_present: titlePresent,
        },
      );

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating title present', error, variables);
      // TODO: handle error by adding the original cdkId back to the store
    },
  });

  const buttonRef = useRef<HTMLDivElement>(null);

  const TITLE_PRESENT_OPTIONS_MAP = useMemo(
    () => getTitlePresentOptionsMap({}),
    [],
  );

  return (
    <ComboboxPopover
      endAdornment={
        // <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_QUEUE}</KeyboardKeyIcon>
        null
      }
      placeholder="Add Title Present..."
      onChange={(value) => {
        const booleanValue = value === 'yes';

        updateTitlePresentMutation.mutate({
          titlePresent: booleanValue,
        });

        setTitlePresent(booleanValue);
      }}
      emptyMessage="No Title Present options"
      options={TITLE_PRESENT_OPTIONS_MAP}
      side="right"
      align="start"
    >
      <div ref={buttonRef}>
        <TitlePresentBadge
          titlePresent={titlePresent}
          useAbbreviation={useAbbreviation}
          className={className}
          hideIcon
        />

        {withHotkeyListener && (
          <HotKeyListener
            hotkeys={HOTKEYS.DEAL_DELIVERY_QUEUE}
            onHotkeyPressed={() => buttonRef.current?.click()}
          />
        )}
      </div>
    </ComboboxPopover>
  );
}
